import DOMPurify from "isomorphic-dompurify";
import { format, isToday, isYesterday, isThisYear, isSameDay } from "date-fns";
import { enUS, fr, nl, de, es } from "date-fns/locale";
import type { Locale } from "date-fns";
import type { ChannelType, InboxConversation, InboxMessage, InboxParticipant } from "@/types/inbox";

const DATE_LOCALES: Record<string, Locale> = { en: enUS, fr, nl, de, es };

function getDateLocale(language?: string): Locale {
	if (!language) return enUS;
	return DATE_LOCALES[language.split("-")[0]] || enUS;
}

/**
 * Normalize a conversation to a single channel identifier.
 * The backend is inconsistent about casing ('email' vs 'EMAIL') and shape,
 * so every channel decision in the inbox MUST go through this helper.
 */
export function getChannel(conversation?: Partial<InboxConversation> | null): ChannelType {
	if (!conversation) return "other";
	const type = (conversation.conversation_type || "").toLowerCase();
	if (type.includes("linkedin")) return "linkedin";
	if (type.includes("whatsapp")) return "whatsapp";
	if (type.includes("email") || type.includes("mail")) return "email";

	const accountType = (conversation.source_account_type || "").toUpperCase();
	if (accountType.includes("LINKEDIN")) return "linkedin";
	if (accountType.includes("WHATSAPP")) return "whatsapp";
	if (accountType.includes("OUTLOOK") || accountType.includes("GOOGLE") || accountType.includes("MAIL")) return "email";

	if (conversation.object === "Email") return "email";
	if (conversation.object === "Chat") return "other";
	return "other";
}

/** Detect whether message content is HTML (vs plain text). */
export function isHTMLContent(content?: string | null): boolean {
	if (!content) return false;
	if (/<[a-z][\s\S]*>/i.test(content)) return true;
	return (
		content.includes("<html") ||
		content.includes("<body") ||
		content.includes("<table") ||
		content.includes("<div")
	);
}

/** Empty Quill document check. */
export function isEmptyRichText(content?: string | null): boolean {
	if (!content) return true;
	const trimmed = content.trim();
	return !trimmed || trimmed === "<p><br></p>";
}

/** Strip HTML down to a single-line plain-text snippet for list previews. */
export function toSnippet(content?: string | null, maxLength = 140): string {
	if (!content) return "";
	const text = isHTMLContent(content)
		? DOMPurify.sanitize(content, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })
		: content;
	const collapsed = text.replace(/\s+/g, " ").trim();
	return collapsed.length > maxLength ? `${collapsed.slice(0, maxLength)}…` : collapsed;
}

export function getParticipantName(participant?: InboxParticipant | null): string {
	if (!participant) return "";
	return (
		participant.name ||
		participant.display_name ||
		participant.displayName ||
		participant.email ||
		participant.identifier ||
		""
	);
}

/**
 * Pick the conversation counterpart to display (avatar/name/context panel).
 * For email we prefer the external 'from' participant; for chats the first
 * attendee (Unipile chat attendee lists exclude the connected account).
 */
export function getPrimaryParticipant(
	conversation: InboxConversation | undefined,
	participants: InboxParticipant[] | undefined
): InboxParticipant | undefined {
	const list = (participants && participants.length > 0 ? participants : conversation?.participants) || [];
	if (list.length === 0) return undefined;
	if (getChannel(conversation) === "email") {
		return list.find((p) => p.role === "from" && getParticipantName(p)) || list.find((p) => getParticipantName(p)) || list[0];
	}
	return list.find((p) => getParticipantName(p)) || list[0];
}

export function getConversationTitle(
	conversation: InboxConversation,
	participants?: InboxParticipant[]
): string {
	const primary = getPrimaryParticipant(conversation, participants);
	const name = getParticipantName(primary);
	if (name) return name;
	return conversation.subject || conversation.id;
}

/** Last message of a conversation, if the list payload includes messages. */
export function getLastMessage(conversation: InboxConversation): InboxMessage | undefined {
	if (conversation.lastMessage) return conversation.lastMessage;
	const messages = conversation.messages;
	if (!messages || messages.length === 0) return undefined;
	return [...messages].sort(
		(a, b) => new Date(a.timestamp || 0).getTime() - new Date(b.timestamp || 0).getTime()
	)[messages.length - 1];
}

export function getConversationTimestamp(conversation: InboxConversation): string | undefined {
	return conversation.last_received_at || getLastMessage(conversation)?.timestamp;
}

/** "14:32" today, "Yesterday", "4 Aug", "4 Aug 24" — for list rows. */
export function formatRelativeTime(
	timestamp: string | undefined,
	language: string,
	yesterdayLabel: string
): string {
	if (!timestamp) return "";
	const date = new Date(timestamp);
	if (Number.isNaN(date.getTime())) return "";
	const locale = getDateLocale(language);
	if (isToday(date)) return format(date, "HH:mm", { locale });
	if (isYesterday(date)) return yesterdayLabel;
	if (isThisYear(date)) return format(date, "d MMM", { locale });
	return format(date, "d MMM yy", { locale });
}

/** Full date-time for message headers and tooltips. */
export function formatFullDateTime(timestamp: string | undefined, language: string): string {
	if (!timestamp) return "";
	const date = new Date(timestamp);
	if (Number.isNaN(date.getTime())) return "";
	return format(date, "d MMM yyyy, HH:mm", { locale: getDateLocale(language) });
}

export function formatMessageTime(timestamp: string | undefined, language: string): string {
	if (!timestamp) return "";
	const date = new Date(timestamp);
	if (Number.isNaN(date.getTime())) return "";
	return format(date, "HH:mm", { locale: getDateLocale(language) });
}

export function formatDayLabel(
	date: Date,
	language: string,
	todayLabel: string,
	yesterdayLabel: string
): string {
	if (isToday(date)) return todayLabel;
	if (isYesterday(date)) return yesterdayLabel;
	const locale = getDateLocale(language);
	return isThisYear(date)
		? format(date, "EEEE d MMMM", { locale })
		: format(date, "d MMMM yyyy", { locale });
}

export function sortMessagesAscending(messages: InboxMessage[]): InboxMessage[] {
	return [...messages].sort(
		(a, b) => new Date(a.timestamp || 0).getTime() - new Date(b.timestamp || 0).getTime()
	);
}

export interface MessageGroup {
	senderAttendeeId: string;
	isSender: boolean;
	sender?: InboxMessage["sender"];
	timestamp?: string;
	messages: InboxMessage[];
}

/** Group consecutive messages from the same sender (chat bubble groups). */
export function groupConsecutiveBySender(messages: InboxMessage[]): MessageGroup[] {
	const sorted = sortMessagesAscending(messages);
	const groups: MessageGroup[] = [];
	let current: MessageGroup | null = null;

	for (const msg of sorted) {
		const senderId = msg.sender_attendee_id || msg.sender?.id || "unknown";
		const isSender = msg.is_sender === 1;
		if (!current || current.senderAttendeeId !== senderId || current.isSender !== isSender) {
			current = { senderAttendeeId: senderId, isSender, sender: msg.sender, timestamp: msg.timestamp, messages: [] };
			groups.push(current);
		}
		current.messages.push(msg);
		if (msg.timestamp) current.timestamp = msg.timestamp;
	}
	return groups;
}

export interface DayGroup<T> {
	date: Date;
	items: T[];
}

/** Group items carrying a timestamp into calendar days (ascending). */
export function groupByDay<T extends { timestamp?: string }>(items: T[]): DayGroup<T>[] {
	const days: DayGroup<T>[] = [];
	for (const item of items) {
		const date = item.timestamp ? new Date(item.timestamp) : new Date(0);
		const last = days[days.length - 1];
		if (last && isSameDay(last.date, date)) {
			last.items.push(item);
		} else {
			days.push({ date, items: [item] });
		}
	}
	return days;
}

export interface SplitEmailContent {
	main: string;
	hasQuotedHistory: boolean;
}

/**
 * Best-effort separation of an email body from its quoted reply history
 * (gmail_quote containers / trailing blockquotes). Falls back to the full
 * content when the structure isn't recognizable.
 */
export function splitQuotedHistory(html: string): SplitEmailContent {
	try {
		const doc = new DOMParser().parseFromString(html, "text/html");
		const quote =
			doc.querySelector(".gmail_quote, .gmail_quote_container, [id^='divRplyFwdMsg']") ||
			doc.body.querySelector(":scope > blockquote, :scope > div > blockquote");
		if (!quote) return { main: html, hasQuotedHistory: false };
		quote.remove();
		const main = doc.body.innerHTML.trim();
		if (!main) return { main: html, hasQuotedHistory: false };
		return { main, hasQuotedHistory: true };
	} catch {
		return { main: html, hasQuotedHistory: false };
	}
}

/** Search filter over conversations (participant names, emails, subject). */
export function filterConversations(
	conversations: InboxConversation[],
	options: { channel: ChannelType | "all"; search: string },
	participantNamesById?: Record<string, string[]>
): InboxConversation[] {
	const search = options.search.trim().toLowerCase();
	return conversations.filter((conversation) => {
		if (options.channel !== "all" && getChannel(conversation) !== options.channel) return false;
		if (!search) return true;
		const haystackParts: string[] = [conversation.subject || ""];
		(conversation.participants || []).forEach((p) => {
			haystackParts.push(getParticipantName(p), p.email || "");
		});
		const cachedNames = participantNamesById?.[conversation.id];
		if (cachedNames) haystackParts.push(...cachedNames);
		return haystackParts.join(" ").toLowerCase().includes(search);
	});
}

/** Extract a LinkedIn public identifier from a profile URL. */
export function extractLinkedInId(profileUrl: string | null | undefined): string | null {
	if (!profileUrl) return null;
	try {
		const url = new URL(profileUrl);
		if (url.hostname.includes("linkedin.com")) {
			const pathParts = url.pathname.split("/");
			const inIndex = pathParts.indexOf("in");
			if (inIndex !== -1 && inIndex + 1 < pathParts.length) {
				return pathParts[inIndex + 1] || null;
			}
		}
	} catch {
		// Invalid URL
	}
	return null;
}
